import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button, Icon, Checkbox } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressCard, faClose, faFile } from "@fortawesome/pro-solid-svg-icons";
import { Column, Table } from "react-virtualized";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import useSite from "../../permits/components/inspections/InspectionPanel/useSite";
import classNames from "classnames";
import { useScrollFadeHorizontal } from "../../global/hooks/useScrollFadeHorizontal";
import { getContact } from "../../../queries/contacts";
import { ContactHistoryItem } from "../../contacts/hooks/useContactHistory";
import { camelCase, compact, mapKeys } from "lodash";

interface PastContactRecordsModalProps {
  open: boolean;
  onClose: () => void;
  contactId: string;
}

type Record = {
  type: "CRM Request" | "Permit";
  caseId: number | string;
  displayId?: string;
  status: string;
  subtype: string;
  dateEntered: string;
};

const COLUMN_WIDTHS = {
  TYPE: 155,
  ID: 100,
  STATUS: 100,
  SUBTYPE: 225,
  DATE: 138,
};

const TOTAL_TABLE_WIDTH = Object.values(COLUMN_WIDTHS).reduce((sum, width) => sum + width, 0);

export const PastContactRecordsModal = ({ open, onClose, contactId }: PastContactRecordsModalProps) => {
  const [allRecords, setAllRecords] = useState<Record[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [crmOnly, setCrmOnly] = useState(true);
  const [contactName, setContactName] = useState<string | null>(null);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { containerClassNames } = useScrollFadeHorizontal(tableContainerRef);

  const networkHeaders = useNetworkHeaders();
  const site = useSite();

  const dateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }),
    [],
  );

  const records = useMemo(() => {
    if (crmOnly) {
      return allRecords.filter((record) => record.type === "CRM Request");
    }
    return allRecords;
  }, [allRecords, crmOnly]);

  const loadRecords = async () => {
    if (loading || !contactId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getContact(networkHeaders)(null, contactId, true);
      const mappedResponse = mapKeys(response?.data?.attributes, (value, key) => camelCase(key));
      const { firstName, lastName, history } = mappedResponse;

      setContactName(compact([lastName, firstName]).join(", "));
      if (history) {
        const historyRecords: Record[] = history.map((item: ContactHistoryItem) => ({
          type: item.type,
          caseId: item.id,
          displayId: "display_id" in item ? item.display_id : item.application_id,
          status: item.status,
          subtype: item.sub_type,
          dateEntered: item.submitted_at,
        }));

        // Sort by date descending
        historyRecords.sort((a, b) => new Date(b.dateEntered).getTime() - new Date(a.dateEntered).getTime());

        setAllRecords(historyRecords);
      } else {
        setAllRecords([]);
      }
    } catch (err) {
      console.error("Error loading contact records:", err);
      setError("Failed to load contact records");
      setAllRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && contactId) {
      setAllRecords([]);
      setError(null);
      loadRecords();
    }
  }, [open, contactId]);

  const rowGetter = ({ index }: { index: number }) => {
    return records[index] || {};
  };

  const buildViewLink = (record: Record) => {
    const host = window.location.origin;
    if (record.type === "CRM Request") {
      return `${host}/${site}/crm/edit/${record.caseId}`;
    } else if (record.type === "Permit") {
      return `${host}/${site}/permits/${record.caseId}`;
    }

    return "#";
  };

  const ROW_HEIGHT = 40;
  const HEADER_HEIGHT = 40;
  const headerClass =
    "tw-font-semibold tw-bg-white-background tw-p-0 tw-px-6 tw-m-0 tw-text-[14px] tw-font-semibold tw-leading-[20px] tw-normal-case tw-tracking-normal tw-flex tw-items-center";
  const cellClass =
    "tw-flex tw-items-center tw-px-6 tw-w-full tw-border-solid tw-border-r-[1px] tw-border-y-0 tw-border-l-0 tw-border-black tw-border-opacity-15 tw-h-[40px]";
  const cellSpanClass =
    "tw-text-[14px] tw-font-semibold tw-p-0 tw-w-full tw-text-ellipsis tw-whitespace-nowrap tw-overflow-hidden";

  const TypeCell = ({ cellData, rowData }: { cellData?: string; rowData: Record }) => (
    <div className={`${cellClass} tw-flex tw-gap-[10px] tw-border-none`}>
      <FontAwesomeIcon
        icon={rowData.type === "CRM Request" ? faAddressCard : faFile}
        className={classNames("tw-m-0 tw-h-[16px] tw-w-[16px] tw-p-0", {
          "tw-text-primary-darker": rowData.type === "CRM Request",
          "tw-text-orange": rowData.type === "Permit",
        })}
      />
      <span className={`${cellSpanClass} tw-overflow-clip`}>{cellData || rowData.type}</span>
    </div>
  );

  const IdCell = ({ rowData }: { cellData?: string; rowData: Record }) => {
    const displayText = rowData.displayId || rowData.caseId;

    return (
      <div className={cellClass}>
        <a
          className={`${cellSpanClass} tw-overflow-visible tw-text-primary-darker tw-underline tw-decoration-solid tw-underline-offset-auto`}
          href={buildViewLink(rowData)}
          target='_blank'
          rel='noopener noreferrer'
        >
          {displayText}
        </a>
      </div>
    );
  };

  const StatusCell = ({ cellData, rowData }: { cellData?: string; rowData: Record }) => (
    <div className={cellClass}>
      <span className={`${cellSpanClass} tw-capitalize`}>{(cellData || rowData.status).replace(/_/g, " ")}</span>
    </div>
  );

  const SubtypeCell = ({ cellData, rowData }: { cellData?: string; rowData: Record }) => (
    <div className={cellClass}>
      <span className={cellSpanClass}>{cellData || rowData.subtype}</span>
    </div>
  );

  const DateCell = ({ cellData, rowData }: { cellData?: string; rowData: Record }) => (
    <div className={`${cellClass} tw-border-none`}>
      <span className={cellSpanClass}>{dateFormat.format(new Date(cellData || rowData.dateEntered))}</span>
    </div>
  );

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} className='tw-center-modal tw-m-0 tw-h-[504px] tw-w-[90%] md:tw-w-[720px]'>
      <div className='tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden'>
        {/* Header */}
        <div className='tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-black tw-border-opacity-10'>
          <div className='tw-flex tw-flex-1 tw-items-center tw-justify-between'>
            <div className='tw-flex tw-min-w-0 tw-flex-grow tw-flex-col tw-flex-wrap tw-justify-start tw-gap-x-4 tw-gap-y-2 tw-py-[13.25px] tw-pl-[24px] md:tw-flex-row md:tw-items-center'>
              <div className='tw-flex tw-min-w-0 tw-flex-shrink tw-gap-2'>
                <h2 className='tw-tg-label-large-accent tw-m-0 tw-max-w-[calc(80vw-80px)] tw-truncate md:tw-max-w-[400px]'>
                  Past Records for {contactName}
                </h2>
                <h2 className='tw-tg-label-large-accent tw-m-0 tw-flex-shrink-0'>({records.length})</h2>
              </div>
              <div className='tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2'>
                <div className='tw-min-w-fit'>
                  <Checkbox
                    toggle
                    label='Display CRM Requests only'
                    checked={crmOnly}
                    onChange={(_, { checked }) => setCrmOnly(!!checked)}
                  />
                </div>
                {loading && <Icon loading name='spinner' className='tw-text-primary' />}
              </div>
            </div>
            <div className='tw-m-0 tw-flex-shrink-0 tw-p-[24px]'>
              <Button
                basic
                icon
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onClose();
                }}
                className='tw-m-0 tw-border-none tw-p-0 tw-shadow-none'
              >
                <FontAwesomeIcon icon={faClose} className='tw-h-[16px] tw-w-[16px]' />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className='tw-flex-grow tw-overflow-hidden tw-p-0'>
          <div className='tw-flex tw-h-full tw-flex-col'>
            {error ? (
              <div className='tw-flex tw-h-32 tw-items-center tw-justify-center'>
                <div className='tw-text-center'>
                  <Icon name='exclamation triangle' size='large' className='tw-text-red-500' />
                  <p className='tw-text-red-600 tw-mt-2'>{error}</p>
                </div>
              </div>
            ) : records.length > 0 ? (
              <div className='tw-flex-1 tw-overflow-hidden' style={{ height: "calc(90vh - 120px)" }}>
                <div className={classNames("tw-h-full", containerClassNames)}>
                  <div
                    className='list-view-container tw-h-full tw-overflow-x-auto tw-overflow-y-hidden'
                    ref={tableContainerRef}
                  >
                    <Table
                      width={TOTAL_TABLE_WIDTH}
                      height={10 * ROW_HEIGHT + HEADER_HEIGHT}
                      headerHeight={HEADER_HEIGHT}
                      rowHeight={ROW_HEIGHT}
                      rowCount={records.length}
                      rowGetter={rowGetter}
                      rowClassName={({ index }) =>
                        classNames("tw-border-none", {
                          "tw-bg-white-background": index % 2 === 1,
                          "tw-p-0 tw-bg-white-background": index < 0,
                        })
                      }
                      style={{ minWidth: `${TOTAL_TABLE_WIDTH}px` }}
                    >
                      <Column
                        label='Type'
                        dataKey='type'
                        width={COLUMN_WIDTHS.TYPE}
                        cellRenderer={TypeCell}
                        headerClassName={headerClass}
                        className='tw-m-0 tw-p-0'
                      />
                      <Column
                        label='ID'
                        dataKey='displayId'
                        width={COLUMN_WIDTHS.ID}
                        cellRenderer={IdCell}
                        headerClassName={headerClass}
                        className='tw-m-0 tw-p-0'
                      />
                      <Column
                        label='Status'
                        dataKey='status'
                        width={COLUMN_WIDTHS.STATUS}
                        cellRenderer={StatusCell}
                        headerClassName={headerClass}
                        className='tw-m-0 tw-p-0'
                      />
                      <Column
                        label='Topic/Subtype'
                        dataKey='subtype'
                        width={COLUMN_WIDTHS.SUBTYPE}
                        cellRenderer={SubtypeCell}
                        headerClassName={headerClass}
                        className='tw-m-0 tw-p-0'
                      />
                      <Column
                        label='Date Entered'
                        dataKey='dateEntered'
                        width={COLUMN_WIDTHS.DATE}
                        cellRenderer={DateCell}
                        headerClassName={`${headerClass} tw-mr-[10px]`}
                        className='tw-m-0 tw-p-0'
                      />
                    </Table>
                  </div>
                </div>
              </div>
            ) : loading ? (
              <div className='tw-flex tw-h-32 tw-items-center tw-justify-center'>
                <Icon loading name='spinner' size='large' className='tw-text-primary' />
                <span className='tw-text-gray-600 tw-ml-2'>Loading records...</span>
              </div>
            ) : (
              <div className='tw-flex tw-h-32 tw-items-center tw-justify-center'>
                <div className='tw-text-center'>
                  <Icon name='search' size='large' className='tw-text-gray-400' />
                  <p className='tw-text-gray-600 tw-mt-2'>No records found for this contact</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
